import React, { useState, useEffect, useMemo, useContext } from 'react'
import { Row, Col, Card, Button, Image } from 'react-bootstrap'
import { useDropzone } from 'react-dropzone'
import classnames from 'classnames'
import PerfectScrollbar from 'react-perfect-scrollbar'
import Swal from 'sweetalert2'
import './styles.css'
import 'assets/scss/plugins/extensions/dropzone.scss'
import 'dropzone/dist/min/dropzone.min.css'

import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.js'
import FileIconImg, { FileIconPreview } from 'components/Accordion/FileIconImg'
import objectPath from 'object-path'
import { DesignContext } from 'contexts/DesignContext'
import { Delete } from '@mui/icons-material'
import Capture from '../button/Capture'
const baseStyle = {
  transition: 'border .24s ease-in-out',
  minWidth: '300px',
  width: '100%',
  height: '200px',
  borderColor: 'rgba(0, 0, 0, 0.3)',
  borderStyle: 'dashed',
}

const activeStyle = {
  borderColor: '#2196f3',
}

const acceptStyle = {
  borderColor: '#00e676',
}

const rejectStyle = {
  borderColor: '#ff1744',
}

const accept = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/msword': ['.doc'],
  'text/plain': ['.txt'],
}

const acceptVideo = {
  'audio/mpeg': ['.mp3'],
  'audio/wav': ['.wav'],
  'audio/webm': ['.webm'],
  'audio/flac': ['.flac'],
  'audio/x-m4a': ['.m4a'],

  'video/mp4': ['.mp4'],
  'video/mpeg': ['.mpeg'],
  'video/webm': ['.webm'],
}

pdfjsLib.GlobalWorkerOptions.workerSrc =
  '/his/libs/pdfjs/2.10.377/pdf.worker.min.js'

const generatePdfThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    fileReader.onload = async (event) => {
      try {
        const typedArray = new Uint8Array(event.target.result)
        const loadingTask = pdfjsLib.getDocument({ data: typedArray })

        const pdf = await loadingTask.promise
        const page = await pdf.getPage(1)
        const viewport = page.getViewport({ scale: 1 })
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        canvas.height = viewport.height
        canvas.width = viewport.width

        await page.render({ canvasContext: context, viewport: viewport })
          .promise
        resolve(canvas.toDataURL())
      } catch (error) {
        reject(error)
      }
    }

    fileReader.readAsArrayBuffer(file)
  })
}

const acceptMap = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  csv: 'text/csv',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  txt: 'text/plain',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  mp4: 'video/mp4',
  avi: 'video/x-msvideo',
}

const DropzoneElement = ({
  id,
  isMultiUpload,
  isReadOnly,
  uischema,
  stateParent,
  allowExt = ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'txt'],
  config,
  isFileUpload,
}) => {
  const designContext = useContext(DesignContext)
  const { setFieldFormData, isUiChanged, setIsUiChanged } = designContext
  const isMulti =
    isMultiUpload || objectPath.get(uischema, 'isMulti') ? true : false
  let connectUiKey = objectPath.get(uischema, 'action.load.key')
  let accept = objectPath.get(uischema, 'accept') || []

  const {
    files: [files, setFiles],
  } = { ...(stateParent || { files: [] }) }

  const [dropFiles, setDropFiles] = useState([])
  const [render, setRender] = useState(false)
  const [openCapture, setOpenCapture] = useState(false)

  const {
    fileRejections,
    getRootProps,
    getInputProps,
    open,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: accept.join(',') || allowExt.map((ext) => acceptMap[ext]).join(','), //'image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain',
    multiple: isMulti,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      new Promise((resolve) => {
        let dropFiles = []
        acceptedFiles.forEach((file) => {
          if (file?.type === 'application/pdf') {
            generatePdfThumbnail(file).then((preview) => {
              Object.assign(file, { preview })
              if (isMulti) {
                dropFiles.push(file)
              } else {
                dropFiles = [file]
              }
            })
          } else {
            Object.assign(file, { preview: URL.createObjectURL(file) })
            if (isMulti) {
              dropFiles.push(file)
            } else {
              dropFiles = [file]
            }
          }
        })
        if (dropFiles.length === 0) {
          dropFiles = acceptedFiles
          resolve(acceptedFiles)
        } else {
          resolve(dropFiles)
        }
      }).then((dropFiles) => {
        setDropFiles(dropFiles)
        setRender(true)
      })
    },
  })

  useEffect(() => {
    if (render) {
      let newFiles = []
      if (isMulti) {
        newFiles = files.concat(dropFiles)
      } else {
        newFiles = dropFiles
      }
      setFiles(newFiles)
      setFieldFormData(uischema.key, newFiles)
      setFieldFormData(uischema.name, newFiles)
      setDropFiles([])
      if (connectUiKey) {
        setFieldFormData(connectUiKey, newFiles)
      }
      setRender(false)
    }
  }, [render])

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file?.path}>
      {file?.path} doesn't match with accepted file type.
    </li>
  ))

  useEffect(() => {
    return () =>
      files.length > 0 &&
      files.forEach((file) => {
        if (file?.preview && file?.type !== 'application/pdf') {
          URL.revokeObjectURL(file?.preview)
        }
      })
  })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  )

  const clearImage = () => {
    if (files.length) {
      files.map((item) => URL.revokeObjectURL(item.preview))
    }
    setFiles([])
  }

  const deleteFile = (deleteIndex) => {
    let newFiles = files.filter((file, index) => index !== deleteIndex)
    newFiles.map((file) => {
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
      return file
    })
    setFiles(newFiles)
  }

  return (
    <React.Fragment>
      <Row>
        {!isReadOnly && (
          <Col>
            <Button
              id={`${id}-dropzone-browse`}
              className="mx-1 float-right"
              variant={'outline-primary'}
              onClick={open}
            >
              <i className="feather icon-folder mr-1" />
              Browse
            </Button>
            {uischema?.isCapture && (
              <>
                <Button
                  id={`${id}-capture`}
                  className="mx-1 float-right"
                  variant={'outline-primary'}
                  onClick={() => setOpenCapture(true)}
                >
                  <i className="feather icon-camera mr-1" />
                  Capture
                </Button>
                {openCapture && (
                  <Capture
                    open={openCapture}
                    onClose={() => setOpenCapture(false)}
                    action={uischema.capture}
                    config={config}
                    isFileUpload={isFileUpload}
                    stateParent={stateParent}
                  />
                )}
              </>
            )}
            <Button
              id={`${id}-dropzone-clear`}
              className="mx-1 float-right"
              variant={'outline-danger'}
              onClick={clearImage}
            >
              <i className="feather icon-trash mr-1" />
              Clear
            </Button>
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          <Card className="px-4 py-2">
            <Row>
              <Col md="12" className="p-0">
                <div
                  {...getRootProps({
                    className: !isReadOnly ? 'dropzone dz-thumb' : '',
                    style,
                  })}
                >
                  <PerfectScrollbar>
                    <input
                      name="media.image"
                      type="file"
                      {...getInputProps()}
                    />
                    {files.length > 0 ? (
                      <div
                        className="dz-thumb w-100 m-0 border-0 "
                        style={{ height: '60px' }}
                      >
                        {files.map((file, index) => (
                          <Row key={index} className="border-bottom">
                            <Col
                              id={`${id}-dropzone-delete-${index}`}
                              sm="auto"
                              onClick={() => deleteFile(index)}
                            >
                              <Delete className="text-danger" />
                            </Col>
                            <Col className="row" onClick={!isReadOnly && open}>
                              <Col sm="auto" className="px-1">
                                <Image
                                  id={`${id}-dropzone-image-${index}`}
                                  src={file?.preview}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'contain',
                                  }}
                                  alt={file?.name}
                                />
                              </Col>
                              <Col className="px-1">
                                <div style={{ margin: 'auto' }}>
                                  <FileIconImg file={file} />
                                  {file?.name} - {file?.size} bytes
                                </div>
                              </Col>
                            </Col>
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <p className="mx-1">
                        <em>
                          <p>
                            Drag 'n' drop some files here, or click Browse to
                            select files
                          </p>
                        </em>
                        <em>
                          (Only{' '}
                          {allowExt.map((name) => {
                            return `*.${name}, `
                          })}{' '}
                          will be accepted)
                        </em>
                      </p>
                    )}
                  </PerfectScrollbar>
                </div>
              </Col>
            </Row>
            {fileRejections.length > 0 && (
              <aside>
                <h4>Rejected files</h4>
                <ul>{fileRejectionItems}</ul>
              </aside>
            )}
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  )
}

export default DropzoneElement
